/**
 * "Link survives sign-in" (NAV-2), the Atelier half.
 *
 * The employee-pool hop carries `returnTo` itself. The Atelier hop does not:
 * `DashboardLayout` bounces to `/sign-in` and Atelier sends the employee back
 * to the root, so a shared deep link (a security alert pointing at the viewer
 * with its filters) would be lost. The SPA keeps the intended path for ten
 * minutes and replays it once after the operator resolves.
 *
 * Pure: the store and the clock are passed in. The stored value is re-checked
 * on the way out, because anything on this origin can write the key, and a
 * replayed absolute URL would be an open redirect.
 */

/** Where the path is kept. A subset of `Storage`, so tests need no browser. */
export interface PathStore {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

export const RETURN_PATH_KEY = 'bo:return-path';
export const RETURN_PATH_TTL_MS = 10 * 60_000;

/** Paths not worth coming back to: the root is where Atelier lands anyway. */
const NOT_WORTH_KEEPING = new Set(['/', '/sign-in']);

/**
 * A path on this origin: one leading slash, then no second slash or backslash
 * (`//host` and `/\host` are both read as another host by browsers).
 */
function isSameOriginPath(path: string): boolean {
    return /^\/(?![/\\])/.test(path) && !path.includes('\\');
}

/** Keep `path` for replay. Silently does nothing for a path that is not this origin's. */
export function rememberReturnPath(store: PathStore, path: string, now: number): void {
    if (!isSameOriginPath(path)) return;
    if (NOT_WORTH_KEEPING.has(path.split('?')[0] ?? path)) return;
    try {
        store.setItem(RETURN_PATH_KEY, JSON.stringify({ path, at: now }));
    } catch {
        // Storage disabled or full: the link is lost, sign-in still works.
    }
}

function readEntry(raw: string | null): { path: string; at: number } | null {
    if (raw === null) return null;
    try {
        const value: unknown = JSON.parse(raw);
        if (typeof value !== 'object' || value === null) return null;
        const { path, at } = value as Record<string, unknown>;
        return typeof path === 'string' && typeof at === 'number' ? { path, at } : null;
    } catch {
        return null;
    }
}

/**
 * The stored path, once: the entry is removed whatever it held. `null` when
 * nothing is stored, it is older than ten minutes, or it is not a path on this
 * origin.
 */
export function takeReturnPath(store: PathStore, now: number): string | null {
    let raw: string | null;
    try {
        raw = store.getItem(RETURN_PATH_KEY);
        store.removeItem(RETURN_PATH_KEY);
    } catch {
        return null;
    }
    const entry = readEntry(raw);
    if (!entry || !isSameOriginPath(entry.path)) return null;
    if (now - entry.at > RETURN_PATH_TTL_MS || now < entry.at) return null;
    return entry.path;
}
